'use client';

import React, { createContext, useContext, useMemo } from 'react';
import { TZDayjs as coreTZDayjs } from '@/util/functions/data/TZDayjs';
import { Dayjs } from 'dayjs';

// Define the shape of the context data
interface TimezoneContextProps {
  timezone: string | null;
}

// Create the context with a default value
const TimezoneContext = createContext<TimezoneContextProps>({
  timezone: null, // Default timezone (e.g., UTC or browser default, handle in useTZDayjs)
});

// Create a provider component
export const TimezoneProvider = ({
  children,
  timezone,
}: {
  children: React.ReactNode;
  timezone: string | null;
}) => {
  //   console.log('timezone', timezone);
  const value = useMemo(() => ({ timezone }), [timezone]);
  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};

// Custom hook to access the timezone directly (optional, if needed elsewhere)
export const useTimezone = (): string | null => {
  const context = useContext(TimezoneContext);
  if (context === undefined) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context.timezone;
};

// Custom hook to get a pre-configured TZDayjs function for client components
export const useTZDayjs = () => {
  const timezone = useTimezone(); // Get timezone from context

  // Return a memoized function that wraps the core TZDayjs
  // This ensures the returned function reference is stable unless the timezone changes
  const memoizedTZDayjs = useMemo(() => {
    // Adjust the returned function signature if needed, though typically
    // the hook consumer just provides the date and optional unix flag.
    return (date?: Parameters<typeof coreTZDayjs>[0], unix = false): Dayjs => {
      // Explicitly pass undefined if timezone context is null
      const tzArg = timezone === null ? undefined : timezone;
      // Call coreTZDayjs with the new parameter order:
      // date, unix (passed from hook consumer), tzArg
      return coreTZDayjs(date, unix, tzArg); // Pass date, unix, tzArg
    };
  }, [timezone]); // Re-memoize only if the timezone changes

  return memoizedTZDayjs;
};
